import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { botsAPI } from '../services/api';
import { Plus, Pencil, Trash2, Code, BarChart2, Bot, X } from 'lucide-react';

function CreateModal({ onClose, onCreate }) {
  const [form, setForm] = useState({
    name: '', description: '', businessName: '', businessType: '',
    aiModel: 'gpt-4o', language: 'en', welcomeMessage: "Hi! 👋 How can I help you today?"
  });
  const [loading, setLoading] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await botsAPI.create(form);
      toast.success(`"${form.name}" created!`);
      onCreate(data.bot);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create chatbot');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <span className="modal-title">Create Chatbot</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={18}/></button>
        </div>
        <form onSubmit={handle} style={{ display:'flex',flexDirection:'column',gap:14 }}>
          {[
            { k:'name',          l:'Bot name *',        t:'text', p:'e.g. Support Bot', req:true },
            { k:'description',   l:'Description',       t:'text', p:'What does this bot do?' },
            { k:'businessName',  l:'Business name',     t:'text', p:'Acme Inc.' },
            { k:'businessType',  l:'Business type',     t:'text', p:'e.g. E-commerce, SaaS' },
            { k:'welcomeMessage',l:'Welcome message',   t:'text', p:'Hi! How can I help?' },
          ].map(f => (
            <div className="form-group" key={f.k}>
              <label className="form-label">{f.l}</label>
              <input className="input" type={f.t} placeholder={f.p} required={!!f.req}
                value={form[f.k]} onChange={e => setForm(v=>({...v,[f.k]:e.target.value}))}/>
            </div>
          ))}
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12 }}>
            <div className="form-group">
              <label className="form-label">AI Model</label>
              <select className="select" value={form.aiModel} onChange={e=>setForm(v=>({...v,aiModel:e.target.value}))}>
                <option value="gpt-4o">GPT-4o</option>
                <option value="gpt-4-turbo">GPT-4 Turbo</option>
                <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Language</label>
              <select className="select" value={form.language} onChange={e=>setForm(v=>({...v,language:e.target.value}))}>
                <option value="en">English</option>
                <option value="ar">Arabic</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
                <option value="de">German</option>
              </select>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Creating…' : 'Create chatbot'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Bots() {
  const [bots, setBots]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    botsAPI.list()
      .then(r => setBots(r.data.bots || []))
      .catch(() => toast.error('Failed to load chatbots'))
      .finally(() => setLoading(false));
  }, []);

  const deleteBot = async (bot) => {
    if (!window.confirm(`Delete "${bot.name}"? This cannot be undone.`)) return;
    try {
      await botsAPI.delete(bot._id);
      setBots(b => b.filter(x => x._id !== bot._id));
      toast.success('Chatbot deleted');
    } catch { toast.error('Delete failed'); }
  };

  if (loading) return <div style={{display:'flex',justifyContent:'center',padding:60}}><div className="spinner"/></div>;

  return (
    <div>
      <div className="page-header" style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:12 }}>
        <div><h1>Chatbots</h1><p>Create and manage your AI chatbots</p></div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={16}/> New Chatbot
        </button>
      </div>

      {bots.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <Bot size={48} style={{ color:'var(--text3)' }}/>
            <h3>No chatbots yet</h3>
            <p>Create your first AI chatbot and embed it on your website to start capturing leads.</p>
            <button className="btn btn-primary" onClick={() => setShowModal(true)} style={{ marginTop:8 }}>
              <Plus size={16}/> Create chatbot
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:16 }}>
          {bots.map(bot => (
            <div key={bot._id} className="card" style={{ display:'flex',flexDirection:'column',gap:16 }}>
              <div style={{ display:'flex',alignItems:'flex-start',gap:12 }}>
                <div style={{ width:44,height:44,borderRadius:'var(--radius)',background:'var(--surface2)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:22,flexShrink:0 }}>
                  {bot.avatarEmoji || '🤖'}
                </div>
                <div style={{ flex:1,minWidth:0 }}>
                  <div style={{ fontFamily:'var(--font-head)',fontWeight:700,fontSize:15,marginBottom:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{bot.name}</div>
                  <div style={{ fontSize:12,color:'var(--text3)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{bot.businessName || bot.description || 'No description'}</div>
                </div>
                <span className={`badge badge-${bot.isActive?'green':'gray'}`}>{bot.isActive?'Active':'Off'}</span>
              </div>

              <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,padding:'10px 0',borderTop:'1px solid var(--border)',borderBottom:'1px solid var(--border)' }}>
                {[
                  ['Knowledge', bot.knowledge?.length || 0],
                  ['Convs', bot.stats?.totalConversations || 0],
                  ['Leads', bot.stats?.totalLeads || 0],
                ].map(([l,v]) => (
                  <div key={l} style={{ textAlign:'center' }}>
                    <div style={{ fontFamily:'var(--font-head)',fontWeight:700,fontSize:18 }}>{v}</div>
                    <div style={{ fontSize:11,color:'var(--text3)' }}>{l}</div>
                  </div>
                ))}
              </div>

              <div style={{ display:'flex',gap:8 }}>
                <button className="btn btn-secondary btn-sm" style={{ flex:1 }} onClick={() => navigate(`/bots/${bot._id}/edit`)}>
                  <Pencil size={13}/> Edit
                </button>
                <button className="btn btn-secondary btn-sm" style={{ flex:1 }} onClick={() => navigate(`/bots/${bot._id}/embed`)}>
                  <Code size={13}/> Embed
                </button>
                <button className="btn btn-ghost btn-sm btn-icon" style={{ color:'var(--red)' }} onClick={() => deleteBot(bot)}>
                  <Trash2 size={14}/>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && <CreateModal onClose={() => setShowModal(false)} onCreate={bot => setBots(b => [bot, ...b])}/>}
    </div>
  );
}
